import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Startseite" },
  { to: "/standorte", label: "Standorte" },
  { to: "/speisekarte", label: "Speisekarte" },
  { to: "/angebote", label: "Angebote" },
  { to: "/helium-vorteile", label: "Helium Club" },
  { to: "/veranstaltungen", label: "Veranstaltungen" },
  { to: "/karriere", label: "Karriere" },
  { to: "/ueber-uns", label: "Über uns" },
  { to: "/kontakt", label: "Kontakt" },
];

export function Logo({ light = false, className = "" }) {
  return (
    <Link to="/" className={`flex items-center gap-2 ${className}`} data-testid="nav-logo">
      <img src="/brand/helium-logo.png" alt="HELIUM" className="h-14 lg:h-16 w-auto object-contain" />
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  const accountLink = user
    ? user.role === "admin" || user.role === "branch_manager" || user.role === "event_manager" || user.role === "marketing_manager"
      ? "/admin"
      : user.role === "employee"
      ? "/scanner"
      : "/mein-konto"
    : "/login";

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}-link`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  loc.pathname === l.to ? "text-[#E0243A]" : "text-gray-700 hover:text-[#E0243A] hover:bg-gray-50"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-2">
                <Link to={accountLink} data-testid="nav-account-btn" className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium text-sm transition-colors">
                  <User className="w-4 h-4" /> {user.first_name || "Mein Konto"}
                </Link>
                <button onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn" className="text-sm text-gray-500 hover:text-[#E0243A]">Abmelden</button>
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn" className="px-4 py-2 rounded-xl text-gray-900 hover:bg-gray-50 font-medium text-sm">Mein Konto</Link>
                <Link to="/registrieren" data-testid="nav-register-btn" className="px-5 py-2.5 rounded-xl bg-[#E0243A] hover:bg-[#C81E33] text-white font-semibold text-sm shadow-md transition-all active:scale-95">Jetzt registrieren</Link>
              </>
            )}
          </div>
          <button className="lg:hidden p-2 rounded-lg hover:bg-gray-100" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-1">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg text-gray-800 font-medium hover:bg-gray-50">{l.label}</Link>
          ))}
          <div className="pt-3 flex gap-2">
            {user ? (
              <>
                <Link to={accountLink} onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-3 rounded-xl bg-gray-100 font-medium">{user.first_name || "Konto"}</Link>
                <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="px-4 py-3 rounded-xl border border-gray-200">Abmelden</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-3 rounded-xl border border-gray-200 font-medium">Anmelden</Link>
                <Link to="/registrieren" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-3 rounded-xl bg-[#E0243A] text-white font-semibold">Registrieren</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
